import { forwardRef, useState } from "react";
import { Button } from "antd";
import { CloudUpload, ExternalLink, FileText, RefreshCw, ShieldCheck } from "lucide-react";
import ResumeVerificationModal from "./ResumeVerificationModal";
import { RESUME_FORMATS_LABEL, RESUME_MAX_SIZE_MB } from "./resumeVerification";
import { TalentAppCard } from "../TalentAppCard";
/**
 * Resume section of the Basic Info step: explains the requirement and
 * opens the verification modal to upload or replace the resume.
 *
 * @param {User} user
 * @param {Boolean} disabled
 * @param {Boolean} showMissingError Highlight that a resume is still needed
 * @param {Function} onVerified Called with (user, file) once a new resume passes
 */
const ResumeRequirementCard = forwardRef(({ user, disabled = false, showMissingError = false, onVerified }, ref) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [verifiedFileName, setVerifiedFileName] = useState(null);
    // Whether a resume was on file when the modal opened (it changes once
    // the new one is verified, but the modal copy shouldn't)
    const [isReplacing, setIsReplacing] = useState(false);

    const hasResume = !!user?.resume_path;
    const isVerified = !!user?.resume_verified_at;
    const resumeUrl = typeof user?.resume_path === 'string' ? user.resume_path : null;
    const resumeName = verifiedFileName || user?.resumeDocument?.()?.name || 'Resume';

    const openModal = () => {
        setIsReplacing(hasResume);
        setIsModalOpen(true);
    };

    const handleVerified = (verifiedUser, file) => {
        setVerifiedFileName(file?.name || null);
        onVerified?.(verifiedUser, file);
    };


    return (
        <TalentAppCard
            className={`${showMissingError && !hasResume ? 'ring-2 ring-[#E54963]' : ''}`}
        >
            <TalentAppCard.Section>
                <div
                    ref={ref}
                    id="resume-requirement"
                // className={`white-panel terms-panel mb-[25px] ${showMissingError && !hasResume ? 'ring-2 ring-[#E54963]' : ''}`}
                >
                    <h5 className="form-header pl-0 mt-0 pb-2">
                        Resume <span className="text-[#E54963]">*</span>
                    </h5>

                    {!hasResume && (
                        <>
                            <p className="m-0 mt-3 text-sm text-gray-600">
                                A readable resume is required to join the Wripple Marketplace. We'll check that your
                                file can be read before saving it.
                            </p>

                            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <Button
                                    type="primary"
                                    size="large"
                                    disabled={disabled}
                                    onClick={openModal}
                                    className="inline-flex text-sm items-center gap-2">
                                    <CloudUpload className="h-4 w-4" aria-hidden />
                                    Upload & verify resume
                                </Button>
                                <span className="text-xs text-gray-500">
                                    {RESUME_FORMATS_LABEL} &middot; up to {RESUME_MAX_SIZE_MB} MB
                                </span>
                            </div>

                            {showMissingError && (
                                <p className="m-0 mt-3 text-sm text-[#E54963]">
                                    Upload a readable resume to continue with your application.
                                </p>
                            )}
                        </>
                    )}

                    {hasResume && (
                        <>
                            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-3 sm:flex-nowrap">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#F7F4FD]">
                                    <FileText className="h-5 w-5 text-primary" aria-hidden />
                                </div>

                                <div className="min-w-0 flex-1">
                                    <p className="m-0 truncate text-sm font-medium text-gray-900">{resumeName}</p>
                                    {isVerified ? (
                                        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-[#16A34A]">
                                            <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Verified
                                        </span>
                                    ) : (
                                        <span className="mt-0.5 inline-block text-xs text-gray-500">On file</span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2">
                                    {resumeUrl && (
                                        <a
                                            href={resumeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                                            <ExternalLink className="h-4 w-4" aria-hidden /> View
                                        </a>
                                    )}
                                    <Button
                                        disabled={disabled}
                                        onClick={openModal}
                                        className="inline-flex items-center gap-1.5">
                                        <RefreshCw className="h-4 w-4" aria-hidden /> Replace
                                    </Button>
                                </div>
                            </div>

                            <p className="m-0 mt-3 text-xs text-gray-500">
                                A replacement is checked before it's saved, and your current resume stays in
                                place unless the new one passes.
                            </p>
                        </>
                    )}

                    <ResumeVerificationModal
                        open={isModalOpen}
                        hasExistingResume={isReplacing}
                        onVerified={handleVerified}
                        onClose={() => setIsModalOpen(false)}
                    />
                </div>
            </TalentAppCard.Section>
        </TalentAppCard>
    );
});

export default ResumeRequirementCard;